'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';

const TOTAL_STEPS = 7;

const WHO_ARE_YOU_OPTIONS = [
  { value: 'brand_in_house', label: 'Brand / In-house Marketing Team', icon: '🏢' },
  { value: 'ecommerce_owner', label: 'E-commerce Store Owner', icon: '🛒' },
  { value: 'freelance_marketer', label: 'Freelance Performance Marketer', icon: '💼' },
  { value: 'marketing_agency', label: 'Marketing Agency', icon: '📊' },
  { value: 'startup', label: 'Startup / Scale-up', icon: '🚀' },
  { value: 'other', label: 'Other', icon: '✨' },
];

const INDUSTRY_OPTIONS = [
  'E-commerce Fashion', 'E-commerce Beauty', 'E-commerce Health', 'E-commerce Electronics',
  'E-commerce Food', 'E-commerce Home', 'SaaS/Software', 'Finance/Insurance',
  'Real Estate', 'Education', 'Travel', 'B2B Services', 'Agency/Consultancy', 'Non-profit', 'Other',
];

const TEAM_SIZE_OPTIONS = [
  { value: 'just_me', label: 'Just me', icon: '👤' },
  { value: '2_5', label: '2–5 people', icon: '👥' },
  { value: '6_20', label: '6–20 people', icon: '🏬' },
  { value: '20_plus', label: '20+ people', icon: '🏭' },
];

const AD_SPEND_OPTIONS = [
  { value: 'under_3k', label: 'Under $3,000', icon: '💵' },
  { value: '3k_15k', label: '$3,000–$15,000', icon: '💰' },
  { value: '15k_50k', label: '$15,000–$50,000', icon: '📈' },
  { value: '50k_plus', label: '$50,000+', icon: '🚀' },
];

const PLATFORM_OPTIONS = [
  { value: 'meta', label: 'Meta Ads', icon: '📘' },
  { value: 'google', label: 'Google Ads', icon: '🔍' },
  { value: 'tiktok', label: 'TikTok Ads', icon: '🎵' },
  { value: 'linkedin', label: 'LinkedIn Ads', icon: '💼' },
  { value: 'snapchat', label: 'Snapchat Ads', icon: '👻' },
  { value: 'other', label: 'Other', icon: '📡' },
];

const CHALLENGE_OPTIONS = [
  { value: 'roas_declining', label: 'ROAS is declining', icon: '📉' },
  { value: 'budget_allocation', label: 'Budget allocation across platforms', icon: '⚖️' },
  { value: 'reporting_time', label: 'Too much time on reporting', icon: '⏱️' },
  { value: 'scaling_profitably', label: 'Scaling profitably', icon: '📊' },
  { value: 'creative_fatigue', label: 'Creative fatigue', icon: '🎨' },
  { value: 'other', label: 'Other', icon: '🔧' },
];

interface QualifierData {
  user_type?: string;
  company_name?: string;
  industry?: string;
  team_size?: string;
  monthly_spend_range?: string;
  selected_platforms?: string[];
  main_challenge?: string;
}

const SESSION_KEY = 'qualifier_data';

function loadFromSession(): QualifierData {
  if (typeof window === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function saveToSession(data: QualifierData) {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
}

export default function QualifierPage() {
  const router = useRouter();
  const [step, setStep] = useState(1);
  const [data, setData] = useState<QualifierData>({});
  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('');
  const [companyError, setCompanyError] = useState('');
  const [industryError, setIndustryError] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);

  useEffect(() => {
    const saved = loadFromSession();
    setData(saved);
    if (saved.company_name) setCompanyName(saved.company_name);
    if (saved.industry) setIndustry(saved.industry);
    if (saved.selected_platforms) setSelectedPlatforms(saved.selected_platforms);
  }, []);

  const updateData = (updates: Partial<QualifierData>) => {
    const newData = { ...data, ...updates };
    setData(newData);
    saveToSession(newData);
  };

  const goBack = () => { if (step > 1) setStep(step - 1); };

  const handleUserType = (value: string) => {
    updateData({ user_type: value });
    setTimeout(() => setStep(2), 200);
  };

  const handleCompanyNext = () => {
    if (companyName.trim().length < 2) { setCompanyError('Company name must be at least 2 characters.'); return; }
    setCompanyError('');
    updateData({ company_name: companyName.trim() });
    setStep(3);
  };

  const handleIndustryNext = () => {
    if (!industry) { setIndustryError('Please select an industry.'); return; }
    setIndustryError('');
    updateData({ industry });
    setStep(4);
  };

  const handleTeamSize = (value: string) => {
    updateData({ team_size: value });
    setTimeout(() => setStep(5), 200);
  };

  const handleAdSpend = (value: string) => {
    updateData({ monthly_spend_range: value });
    setTimeout(() => setStep(6), 200);
  };

  const togglePlatform = (value: string) => {
    const updated = selectedPlatforms.includes(value)
      ? selectedPlatforms.filter((p) => p !== value)
      : [...selectedPlatforms, value];
    setSelectedPlatforms(updated);
  };

  const handlePlatformsNext = () => {
    updateData({ selected_platforms: selectedPlatforms });
    setStep(7);
  };

  const handleChallenge = (value: string) => {
    updateData({ main_challenge: value });
    setTimeout(() => router.push('/register'), 200);
  };

  const progressPercent = (step / TOTAL_STEPS) * 100;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-start px-4 py-8">
      <div className="flex items-center gap-2 mb-8">
        <AppLogo size={32} />
        <span className="font-semibold text-[#1E293B] text-xl tracking-tight">Incade</span>
        <span className="text-orange-500 text-sm font-mono font-semibold">.io</span>
      </div>

      <div className="w-full max-w-lg">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-slate-500">Step {step} of {TOTAL_STEPS}</span>
            <span className="text-xs font-medium text-slate-400">{Math.round(progressPercent)}% complete</span>
          </div>
          <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div className="h-full bg-orange-500 rounded-full transition-all duration-500 ease-out" style={{ width: `${progressPercent}%` }} />
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
          {step > 1 && (
            <button onClick={goBack} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6 transition-colors">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
          )}

          {step === 1 && (
            <div>
              <h1 className="text-xl font-semibold text-slate-900 mb-1">Let us personalize your experience</h1>
              <p className="text-sm text-slate-500 mb-6">Who are you?</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {WHO_ARE_YOU_OPTIONS.map((opt) => (
                  <button key={opt.value} onClick={() => handleUserType(opt.value)}
                    className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border text-left transition-all duration-150 hover:border-orange-400 hover:bg-orange-50 ${data.user_type === opt.value ? 'border-orange-500 bg-orange-50 ring-2 ring-orange-400/30' : 'border-slate-200 bg-white'}`}>
                    <span className="text-xl">{opt.icon}</span>
                    <span className="text-sm font-medium text-slate-700">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h1 className="text-xl font-semibold text-slate-900 mb-1">What is your company called?</h1>
              <p className="text-sm text-slate-500 mb-6">Minimum 2 characters.</p>
              <div className="mb-5">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Company name</label>
                <input type="text" value={companyName} onChange={(e) => { setCompanyName(e.target.value); if (companyError) setCompanyError(''); }}
                  onKeyDown={(e) => e.key === 'Enter' && handleCompanyNext()} placeholder="e.g. Acme Corp" autoFocus
                  className={`w-full px-3.5 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400 transition-colors ${companyError ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`} />
                {companyError && <p className="mt-1.5 text-xs text-red-600">{companyError}</p>}
              </div>
              <button onClick={handleCompanyNext} className="w-full btn-primary py-2.5 text-sm font-semibold">Next</button>
            </div>
          )}

          {step === 3 && (
            <div>
              <h1 className="text-xl font-semibold text-slate-900 mb-1">What industry are you in?</h1>
              <p className="text-sm text-slate-500 mb-6">Select the one that best describes your business.</p>
              <div className="mb-5">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Industry</label>
                <select value={industry} onChange={(e) => { setIndustry(e.target.value); if (industryError) setIndustryError(''); }}
                  className={`w-full px-3.5 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400 transition-colors bg-white ${industryError ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}>
                  <option value="">Select an industry…</option>
                  {INDUSTRY_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                </select>
                {industryError && <p className="mt-1.5 text-xs text-red-600">{industryError}</p>}
              </div>
              <button onClick={handleIndustryNext} className="w-full btn-primary py-2.5 text-sm font-semibold">Next</button>
            </div>
          )}

          {step === 4 && (
            <div>
              <h1 className="text-xl font-semibold text-slate-900 mb-1">How big is your team?</h1>
              <p className="text-sm text-slate-500 mb-6">Select your team size.</p>
              <div className="grid grid-cols-2 gap-3">
                {TEAM_SIZE_OPTIONS.map((opt) => (
                  <button key={opt.value} onClick={() => handleTeamSize(opt.value)}
                    className={`flex flex-col items-center gap-2 px-4 py-5 rounded-xl border text-center transition-all duration-150 hover:border-orange-400 hover:bg-orange-50 ${data.team_size === opt.value ? 'border-orange-500 bg-orange-50 ring-2 ring-orange-400/30' : 'border-slate-200 bg-white'}`}>
                    <span className="text-2xl">{opt.icon}</span>
                    <span className="text-sm font-medium text-slate-700">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 5 && (
            <div>
              <h1 className="text-xl font-semibold text-slate-900 mb-1">What is your monthly ad spend?</h1>
              <p className="text-sm text-slate-500 mb-6">This helps us recommend the right plan.</p>
              <div className="grid grid-cols-2 gap-3">
                {AD_SPEND_OPTIONS.map((opt) => (
                  <button key={opt.value} onClick={() => handleAdSpend(opt.value)}
                    className={`flex flex-col items-center gap-2 px-4 py-5 rounded-xl border text-center transition-all duration-150 hover:border-orange-400 hover:bg-orange-50 ${data.monthly_spend_range === opt.value ? 'border-orange-500 bg-orange-50 ring-2 ring-orange-400/30' : 'border-slate-200 bg-white'}`}>
                    <span className="text-2xl">{opt.icon}</span>
                    <span className="text-sm font-medium text-slate-700">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 6 && (
            <div>
              <h1 className="text-xl font-semibold text-slate-900 mb-1">Which ad platforms do you use?</h1>
              <p className="text-sm text-slate-500 mb-6">Select all that apply.</p>
              <div className="grid grid-cols-2 gap-3 mb-6">
                {PLATFORM_OPTIONS.map((opt) => {
                  const isSelected = selectedPlatforms.includes(opt.value);
                  return (
                    <button key={opt.value} onClick={() => togglePlatform(opt.value)}
                      className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border text-left transition-all duration-150 hover:border-orange-400 hover:bg-orange-50 ${isSelected ? 'border-orange-500 bg-orange-50 ring-2 ring-orange-400/30' : 'border-slate-200 bg-white'}`}>
                      <span className="text-xl">{opt.icon}</span>
                      <span className="text-sm font-medium text-slate-700">{opt.label}</span>
                      {isSelected && (
                        <svg className="w-4 h-4 text-orange-500 ml-auto flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
              <button onClick={handlePlatformsNext} disabled={selectedPlatforms.length === 0}
                className="w-full btn-primary py-2.5 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed">Next</button>
            </div>
          )}

          {step === 7 && (
            <div>
              <h1 className="text-xl font-semibold text-slate-900 mb-1">What is your biggest challenge?</h1>
              <p className="text-sm text-slate-500 mb-6">We&apos;ll tailor your experience around this.</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {CHALLENGE_OPTIONS.map((opt) => (
                  <button key={opt.value} onClick={() => handleChallenge(opt.value)}
                    className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border text-left transition-all duration-150 hover:border-orange-400 hover:bg-orange-50 ${data.main_challenge === opt.value ? 'border-orange-500 bg-orange-50 ring-2 ring-orange-400/30' : 'border-slate-200 bg-white'}`}>
                    <span className="text-xl">{opt.icon}</span>
                    <span className="text-sm font-medium text-slate-700">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-sm text-slate-500">
          Already have an account?{' '}
          <a href="/login" className="text-orange-500 hover:text-orange-600 font-medium">Sign in</a>
        </p>
      </div>
    </div>
  );
}
